import { useEffect, useId, useRef, useState } from "react"
import { ArrowLeft, CheckCircle2, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "@f2e/ui/components/base"
import { Agent, AgentType, Workspace } from "@/types"
import { useStore } from "@/store"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"

import FormFactory from "../Agent/FormFactory"
import { BasicFormType } from "../Agent/type"
import {
  useListKnowledgeBaseNodesApi,
  knowledgeNodeResponseToKnowledgeBaseFolder,
} from "@/hooks/api"
import { AgentHistoryDropdownState } from "@/hooks/useAgentHistories"
import { TemplateCreatePanel } from "../Agent/templates/TemplateCreatePanel"
import type { TemplateTransitionReason } from "../Agent/templates/TemplateCreatePanel"
import { resolveAgentTemplateLocale } from "../Agent/templates/registry"
import type { AgentTemplateDraft } from "../Agent/templates/types"

export interface ConfigPanelProps {
  formId?: string
  agent?: Agent
  agentType: AgentType
  workspace: Workspace
  agents: Agent[] // Add agents prop
  onConfirm?: (agentType: AgentType, formData: BasicFormType) => void
  historiesState: AgentHistoryDropdownState
  onDirtyChange?: (dirty: boolean) => void
  onUnsavedChangesChange?: (dirty: boolean) => void
  onTemplatePanelOpenChange?: (open: boolean) => void
  isCreating?: boolean
}

const ConfigPanel = ({
  formId,
  agent,
  agentType,
  workspace,
  agents,
  onConfirm,
  historiesState,
  onDirtyChange,
  onUnsavedChangesChange,
  onTemplatePanelOpenChange,
  isCreating = false,
}: ConfigPanelProps) => {
  const { t, i18n } = useTranslation()
  const reviewFormId = useId()
  const agentId = agent?.id
  const [knowledgeBaseMap, setKnowledgeBaseMap] = useState<
    Map<string, { id: string; name: string }>
  >(new Map())
  const [templatePanelOpen, setTemplatePanelOpen] = useState(!agent)
  const [templateResult, setTemplateResult] = useState<AgentTemplateDraft>()
  const [isEditorDirty, setIsEditorDirty] = useState(false)
  const [isTemplateDirty, setIsTemplateDirty] = useState(false)
  const [isReviewDirty, setIsReviewDirty] = useState(false)
  const [isTemplateBusy, setIsTemplateBusy] = useState(false)
  const [pendingTransition, setPendingTransition] = useState<{
    action: () => void
    reason: TemplateTransitionReason
  } | null>(null)
  const [pendingGeneration, setPendingGeneration] = useState<
    (() => void) | null
  >(null)
  const templateLauncherRef = useRef<HTMLButtonElement>(null)
  const templateCloseRef = useRef<HTMLButtonElement>(null)
  const previousTemplatePanelOpenRef = useRef(templatePanelOpen)
  const currentOrganization = useStore((s) => s.currentOrganization)
  const organizationId = currentOrganization?.id

  const { fetchFn: listKnowledgeNodes } = useListKnowledgeBaseNodesApi()

  useEffect(() => {
    onTemplatePanelOpenChange?.(templatePanelOpen)
  }, [onTemplatePanelOpenChange, templatePanelOpen])

  useEffect(() => {
    let ownsResponse = true
    setKnowledgeBaseMap(new Map())
    if (!organizationId) return () => undefined

    void listKnowledgeNodes({
      urlBuilderParams: {
        organizationId,
        workspaceId: workspace.id,
      },
    })
      .then((response) => {
        if (!ownsResponse || !response?.data) return
        setKnowledgeBaseMap(
          new Map(
            response.data
              .filter((node) => node.node_type === "FOLDER")
              .map((node) => {
                const knowledgeBase =
                  knowledgeNodeResponseToKnowledgeBaseFolder(node)
                return [knowledgeBase.id, knowledgeBase]
              })
          )
        )
      })
      .catch(() => {
        if (ownsResponse) setKnowledgeBaseMap(new Map())
      })

    return () => {
      ownsResponse = false
    }
    // Intentionally exclude fetchFn dep (listKnowledgeNodes) to avoid repeated fetching
    // when hook returns a new function identity across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.id, organizationId])

  useEffect(() => {
    setTemplatePanelOpen(agentId === undefined)
    setTemplateResult(undefined)
    setIsEditorDirty(false)
    setIsTemplateDirty(false)
    setIsReviewDirty(false)
    setIsTemplateBusy(false)
    setPendingTransition(null)
    setPendingGeneration(null)
    onDirtyChange?.(false)
  }, [agentId, onDirtyChange, workspace.id])

  useEffect(() => {
    onUnsavedChangesChange?.(isEditorDirty || isTemplateDirty || isReviewDirty)
  }, [isEditorDirty, isTemplateDirty, isReviewDirty, onUnsavedChangesChange])

  useEffect(() => {
    if (previousTemplatePanelOpenRef.current === templatePanelOpen) return
    previousTemplatePanelOpenRef.current = templatePanelOpen
    if (templatePanelOpen) {
      templateCloseRef.current?.focus()
    } else {
      templateLauncherRef.current?.focus()
    }
  }, [templatePanelOpen])

  const handleSetupDirtyChange = (dirty: boolean) => {
    setIsTemplateDirty(dirty)
  }

  const handleEditorDirtyChange = (dirty: boolean) => {
    setIsEditorDirty(dirty)
    onDirtyChange?.(dirty)
  }

  const requestTransition = (
    transition: () => void,
    reason: TemplateTransitionReason
  ) => {
    if (isTemplateDirty || isReviewDirty) {
      setPendingTransition({ action: transition, reason })
      return
    }
    transition()
  }

  const requestGeneration = (generate: () => void) => {
    if (isEditorDirty || isReviewDirty || templateResult) {
      setPendingGeneration(() => generate)
      return
    }
    generate()
  }

  const templateLocale = resolveAgentTemplateLocale(i18n.resolvedLanguage)
  return (
    <div className="px-2">
      <CopilotHiddenField
        fieldKey="agentCreation.activePanel"
        value={!agent && templatePanelOpen ? "template" : "editor"}
      />
      <div className="grid">
        <div
          className="col-start-1 row-start-1"
          aria-hidden={!agent && templatePanelOpen}
          inert={!agent && templatePanelOpen ? true : undefined}
        >
          {!agent && !templatePanelOpen && templateResult && (
            <div
              className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4"
              role="status"
            >
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:justify-between">
                <div className="flex gap-3">
                  <div className="mt-0.5 rounded-full bg-emerald-500/10 p-1.5 text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-950 dark:text-emerald-50">
                      {t("workspace.agent.templates.generated")}
                    </p>
                    <p className="text-sm text-emerald-950/70 dark:text-emerald-100/75">
                      {t("workspace.agent.templates.generatedDescription")}
                    </p>
                  </div>
                </div>
                <Button
                  ref={templateLauncherRef}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-full border-emerald-500/30 bg-background/80 hover:bg-emerald-500/10 sm:w-auto"
                  onClick={() => setTemplatePanelOpen(true)}
                >
                  {t("workspace.agent.templates.changeSetup")}
                </Button>
              </div>
            </div>
          )}
          {!agent && !templatePanelOpen && !templateResult && (
            <div className="mb-6 flex flex-col items-start gap-4 rounded-xl border border-border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium">
                  {t("workspace.agent.templates.templatePath")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("workspace.agent.templates.templateCtaDescription")}
                </p>
              </div>
              <Button
                ref={templateLauncherRef}
                type="button"
                size="sm"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setTemplatePanelOpen(true)}
              >
                <Sparkles className="mr-2 size-4" />
                {t("workspace.agent.templates.templatePath")}
              </Button>
            </div>
          )}
          <FormFactory
            formId={formId}
            agentType={agentType}
            agent={agent}
            workspace={workspace}
            knowledgeBaseMap={knowledgeBaseMap}
            agents={agents}
            initialValues={!agent ? templateResult : undefined}
            copilotApplyEnabled={Boolean(agent) || !templatePanelOpen}
            flatMode={true}
            onConfirm={onConfirm}
            historiesState={historiesState}
            onDirtyChange={handleEditorDirtyChange}
          />
        </div>

        {!agent && (
          <div
            hidden={!templatePanelOpen}
            className="col-start-1 row-start-1 z-10 min-h-full bg-background motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200"
          >
            {!templateResult && (
              <div className="mb-6 flex justify-end border-b border-border pb-4">
                <Button
                  ref={templateCloseRef}
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isTemplateBusy || isCreating}
                  onClick={() => setTemplatePanelOpen(false)}
                >
                  <ArrowLeft className="mr-2 size-4" />
                  {t("workspace.agent.templates.blankPath")}
                </Button>
              </div>
            )}
            <TemplateCreatePanel
              key={workspace.id}
              workspaceId={workspace.id}
              locale={templateLocale}
              hasGeneratedDraft={Boolean(templateResult)}
              onDirtyChange={handleSetupDirtyChange}
              onRequestTransition={requestTransition}
              onRequestGenerate={requestGeneration}
              onBusyChange={setIsTemplateBusy}
              onDraftGenerated={(result) => {
                setTemplateResult(result)
                setIsReviewDirty(false)
              }}
              onDraftCleared={() => {
                setTemplateResult(undefined)
                setIsReviewDirty(false)
              }}
              reviewFormId={reviewFormId}
              isCreating={isCreating}
              renderReviewForm={(draft) => (
                <FormFactory
                  formId={reviewFormId}
                  agentType={agentType}
                  workspace={workspace}
                  knowledgeBaseMap={knowledgeBaseMap}
                  agents={agents}
                  initialValues={draft}
                  copilotApplyEnabled
                  onConfirm={onConfirm}
                  onDirtyChange={setIsReviewDirty}
                />
              )}
            />
          </div>
        )}
      </div>

      <AlertDialog
        open={pendingTransition !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTransition(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("workspace.agent.templates.discardChangesTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                pendingTransition?.reason === "back-to-setup"
                  ? "workspace.agent.templates.discardReviewDescription"
                  : "workspace.agent.templates.discardTemplateDescription"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("workspace.agent.templates.discardChangesCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const transition = pendingTransition?.action
                setPendingTransition(null)
                transition?.()
              }}
            >
              {t("workspace.agent.templates.discardChangesConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingGeneration !== null}
        onOpenChange={(open) => {
          if (!open) setPendingGeneration(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("workspace.agent.templates.overwriteDraftTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("workspace.agent.templates.overwriteDraftDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("workspace.agent.templates.overwriteDraftCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const generate = pendingGeneration
                setPendingGeneration(null)
                generate?.()
              }}
            >
              {t("workspace.agent.templates.overwriteDraftConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export { ConfigPanel }
export default ConfigPanel
